import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "../../lib/utils"
import { useAuth } from "../../contexts/AuthContext"
import { User, LogOut, Settings, Home, Menu, X } from "lucide-react"

export function NavBar({ items, className, onSignInClick }) {
  const [activeTab, setActiveTab] = useState(items[0]?.name)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = React.useRef(null)

  // Sync active tab with current URL
  useEffect(() => {
    const path = window.location.pathname;
    const match = items.find(item => {
      if (item.url === '/') return path === '/';
      return path.startsWith(item.url) && item.url !== '/#' && !item.url.startsWith('#');
    });
    if (match) {
      setActiveTab(match.name);
    }
  }, [items]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div
      ref={menuRef}
      className={cn(
        "fixed top-6 right-6 md:right-8 z-[100] pointer-events-auto flex flex-col items-end gap-2",
        className
      )}
    >
      <div className="bg-white/10 border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.3)] backdrop-blur-3xl rounded-3xl p-1.5 flex flex-col gap-1.5 min-w-[140px]">
        {/* Toggle (Menu) */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={cn(
            "w-full relative cursor-pointer text-[10px] md:text-[11px] font-black uppercase tracking-widest px-4 py-2.5 rounded-full transition-all duration-300 flex items-center justify-between",
            menuOpen ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"
          )}
        >
          <span className="relative z-10 flex items-center gap-1.5">
            <Menu className="w-3.5 h-3.5" />
            MENU
          </span>
          <motion.div 
            animate={{ rotate: menuOpen ? 180 : 0 }} 
            className="text-white/50 text-[9px] ml-2"
          >
            ▼
          </motion.div>
        </button>

        {/* Expanded Items */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="flex flex-col gap-1.5 overflow-hidden"
            >
              {items.map((item) => {
                const isActive = activeTab === item.name
                return (
                  <a
                    key={item.name}
                    href={item.url}
                    onClick={(e) => {
                      if (item.url.startsWith('#')) {
                        e.preventDefault()
                        const target = document.querySelector(item.url)
                        if (target) {
                           target.scrollIntoView({ behavior: 'smooth' })
                        }
                      }
                      setActiveTab(item.name)
                      setMenuOpen(false)
                    }}
                    className={cn(
                      "w-full relative cursor-pointer text-[10px] md:text-[11px] font-black uppercase tracking-widest px-4 py-2.5 rounded-full transition-all duration-300 block text-left",
                      isActive
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-white/60 hover:bg-white/10 hover:text-white"
                    )}
                  >
                    <span className="relative z-10">{item.name}</span>
                  </a>
                )
              })}
              {/* Sign In Button */}
              {onSignInClick && (
                <>
                  <div className="h-[1px] w-full bg-white/10 my-1"></div>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onSignInClick();
                    }}
                    className="w-full relative cursor-pointer text-[10px] md:text-[11px] font-black uppercase tracking-widest px-4 py-2.5 rounded-full transition-all duration-300 block text-left text-white/60 hover:bg-white/10 hover:text-white"
                  >
                    <span className="relative z-10">Sign In</span>
                  </button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
